import { useState, useEffect, useCallback, useRef, memo, lazy, Suspense, useMemo } from "react";
import { SearchIcon as Search, ShoppingBagIcon as ShoppingBag, HeartIcon as Heart, MenuIcon as Menu, XIcon as X, UserIcon as User, ChevronDownIcon as ChevronDown, LogOutIcon as LogOut, PackageIcon as Package } from "./icons";
// Lazy-load supabase to avoid 173KB SDK in critical bundle
const getSupabase = () => import("@/integrations/supabase/client").then(m => m.supabase);
import { useNavigate, useLocation } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { prefetchProductsForPath } from "@/lib/prefetchProducts";
import { useMenuCategories } from "@/hooks/useCategories";
import { useCart } from "@/contexts/CartContext";
import { useLanguage } from "@/contexts/LanguageContext";
import defaultLogo from "@/assets/logo.webp";
import { useSiteContent } from "@/hooks/useSiteContent";
import { useSiteTheme } from "@/hooks/useSiteTheme";
import { navigateToStorefrontHome, scrollToStorefrontTarget, buildStorefrontHomePath } from "@/lib/storefrontNavigation";
const SearchModal = lazy(() => import("./SearchModal"));

/** Convert hex to HSL string for CSS variable compatibility */
const hexToHslHeader = (hex: string): string | null => {
  if (!hex || !/^#[0-9a-fA-F]{6}$/.test(hex)) return null;
  const r = parseInt(hex.slice(1, 3), 16) / 255;
  const g = parseInt(hex.slice(3, 5), 16) / 255;
  const b = parseInt(hex.slice(5, 7), 16) / 255;
  const max = Math.max(r, g, b), min = Math.min(r, g, b);
  let h = 0, s = 0;
  const l = (max + min) / 2;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    if (max === r) h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
    else if (max === g) h = ((b - r) / d + 2) / 6;
    else h = ((r - g) / d + 4) / 6;
  }
  return `${Math.round(h * 360)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
};

/** Dark overrides for header when a light design is active */
const darkHeaderStyles: React.CSSProperties = {
  "--background": "0 0% 0%",
  "--foreground": "43 56% 52%",
  "--primary": "43 56% 52%",
  "--primary-foreground": "0 0% 0%",
  "--secondary": "0 0% 3%",
  "--secondary-foreground": "43 56% 52%",
  "--muted": "0 0% 3%",
  "--muted-foreground": "43 30% 40%",
  "--accent": "43 56% 52%",
  "--accent-foreground": "0 0% 0%",
  "--border": "43 30% 20%",
  "--input": "43 30% 20%",
  "--ring": "43 56% 52%",
  "--card": "0 0% 5%",
  "--card-foreground": "43 56% 52%",
  "--popover": "0 0% 5%",
  "--popover-foreground": "43 56% 52%",
} as any;

const Header = memo(() => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [user, setUser] = useState<any>(null);
  const { isLightDesign } = useSiteTheme();
  const { content: siteSettings, loading: settingsLoading } = useSiteContent();
  const logoUrl = siteSettings.logo_url?.trim() || (!settingsLoading ? defaultLogo : "");
  const logoReady = !settingsLoading && Boolean(logoUrl);
  const [catDropdownOpen, setCatDropdownOpen] = useState(false);
  const [mobileCatOpen, setMobileCatOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const shouldLoadMenuCategories = catDropdownOpen || mobileOpen;
  const { data: categories = [] } = useMenuCategories(shouldLoadMenuCategories);
  const userMenuRef = useRef<HTMLDivElement>(null);
  const mobileMenuRef = useRef<HTMLDivElement>(null);
  const mobileToggleRef = useRef<HTMLButtonElement>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { openCart, closeCart, totalItems } = useCart();
  const { t } = useLanguage();
  const queryClient = useQueryClient();

  const handlePrefetch = useCallback((target: string) => {
    prefetchProductsForPath(`/${target}`, queryClient);
  }, [queryClient]);

  // Scroll listener with passive flag
  useEffect(() => {
    let ticking = false;
    const onScroll = () => {
      if (!ticking) {
        ticking = true;
        requestAnimationFrame(() => {
          setScrolled(window.scrollY > 20);
          ticking = false;
        });
      }
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Auth listener — keep completely interaction-driven on homepage to protect LCP
  useEffect(() => {
    let cleanupAuth = () => {};
    let initialized = false;

    const setupAuth = async () => {
      if (initialized) return;
      initialized = true;
      const supabase = await getSupabase();
      const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
        setUser(session?.user ?? null);
      });
      supabase.auth.getSession().then(({ data: { session } }) => {
        setUser(session?.user ?? null);
      });
      cleanupAuth = () => subscription.unsubscribe();
    };

    const interactionEvents: Array<keyof WindowEventMap> = ["pointerdown", "touchstart", "keydown", "scroll"];
    const onInteract = () => {
      interactionEvents.forEach((eventName) => window.removeEventListener(eventName, onInteract));
      setupAuth();
    };

    interactionEvents.forEach((eventName) => window.addEventListener(eventName, onInteract, { once: true, passive: true }));

    if (location.pathname !== "/") {
      // Non-homepage: setup auth immediately via idle callback (no 500ms delay)
      if (typeof window.requestIdleCallback === "function") {
        const idleId = window.requestIdleCallback(() => onInteract(), { timeout: 1000 });
        return () => {
          window.cancelIdleCallback?.(idleId);
          interactionEvents.forEach((eventName) => window.removeEventListener(eventName, onInteract));
          cleanupAuth();
        };
      }
      // Fallback: immediate
      onInteract();
      return () => { cleanupAuth(); };
    }

    return () => {
      interactionEvents.forEach((eventName) => window.removeEventListener(eventName, onInteract));
      cleanupAuth();
    };
  }, [location.pathname]);

  // Click outside to close user menu & mobile menu
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target as Node)) {
        setUserMenuOpen(false);
      }
      if (mobileOpen && mobileMenuRef.current && !mobileMenuRef.current.contains(e.target as Node) && mobileToggleRef.current && !mobileToggleRef.current.contains(e.target as Node)) {
        setMobileOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [mobileOpen]);

  const showReviews = siteSettings.show_reviews_in_header === "true";

  const navLinks = [
    { label: t("nav_home"), target: "home", key: "home" },
    { label: t("nav_shop"), target: "products", isPage: true, key: "shop" },
    { label: t("nav_categories"), target: "categories", isDropdown: true, key: "categories" },
    { label: t("nav_best_selling"), target: "best-selling", isPage: true, key: "best-selling" },
    { label: t("nav_new_arrival", "New Arrival"), target: "new-arrivals", isPage: true, key: "new-arrivals" },
    { label: t("all_products", "All Products"), target: "products", isPage: true, key: "all-products" },
    ...(showReviews ? [{ label: t("nav_reviews", "Reviews"), target: "reviews", isPage: true, key: "reviews" }] : []),
    { label: t("nav_contact"), target: "contact", key: "contact" },
  ];

  const scrollToSection = useCallback((target: string) => {
    if (target === "home") {
      setMobileOpen(false);
      closeCart();
      if (location.pathname === "/") {
        window.scrollTo({ top: 0, behavior: "smooth" });
        return;
      }
      navigateToStorefrontHome(navigate, location.search, { scrollToTop: true });
      return;
    }

    setMobileOpen(false);
    closeCart();

    if (target === "contact") {
      if (location.pathname !== "/") {
        navigateToStorefrontHome(navigate, location.search, { scrollTarget: "contact" });
      } else {
        const footer = document.getElementById("contact");
        if (footer) {
          footer.scrollIntoView({ behavior: "smooth", block: "start" });
        } else {
          window.scrollTo({ top: document.body.scrollHeight, behavior: "smooth" });
          setTimeout(() => {
            const el = document.getElementById("contact");
            if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
          }, 800);
        }
      }
      return;
    }

    if (location.pathname !== "/") {
      navigateToStorefrontHome(navigate, location.search, { scrollTarget: target });
    } else {
      scrollToStorefrontTarget(target);
    }
  }, [location.pathname, location.search, navigate, closeCart]);

  const handleCategoryClick = useCallback((slug: string) => {
    setCatDropdownOpen(false);
    setMobileCatOpen(false);
    setMobileOpen(false);
    navigate(`/category/${slug}`);
  }, [navigate]);

  // Build custom header color styles from site_settings
  const customHeaderStyles = useMemo((): React.CSSProperties | undefined => {
    const bg = siteSettings.header_bg_color?.trim();
    const text = siteSettings.header_text_color?.trim();
    if (!bg && !text) return isLightDesign ? darkHeaderStyles : undefined;
    const s: Record<string, string> = {};
    if (bg) s.backgroundColor = bg;
    if (text) {
      s.color = text;
      // Convert hex to HSL for CSS variables used by Tailwind (hsl(var(--foreground)))
      const hsl = hexToHslHeader(text);
      if (hsl) {
        s["--foreground" as any] = hsl;
        s["--primary" as any] = hsl;
        s["--muted-foreground" as any] = hsl;
      }
    }
    return s as React.CSSProperties;
  }, [siteSettings.header_bg_color, siteSettings.header_text_color, isLightDesign]);

  return (
    <>
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled
          ? `${!customHeaderStyles?.backgroundColor ? "bg-background/95" : ""} backdrop-blur-lg shadow-[0_2px_20px_hsl(43_56%_52%/0.08)] border-b border-border`
          : `${!customHeaderStyles?.backgroundColor ? "bg-background/70" : ""} backdrop-blur-sm border-b border-transparent`
      }`}
      style={customHeaderStyles}
    >
      {/* Top accent line — premium gold gradient */}
      <div className="h-[1px] w-full bg-gradient-to-r from-transparent via-primary/60 to-transparent" />

      {/* ============ MOBILE / TABLET HEADER (unchanged single-row) ============ */}
      <div className="lg:hidden container mx-auto grid grid-cols-[1fr_auto_1fr] items-center h-[60px] px-3 gap-2">
        {/* LEFT: Mobile menu toggle + Search */}
        <div className="flex items-center justify-start gap-0.5 min-w-0">
          <button
            ref={mobileToggleRef}
            aria-label="Menu"
            className="p-2.5 rounded-full text-primary hover:bg-primary/10 transition-all duration-300"
            onClick={() => setMobileOpen(!mobileOpen)}
          >
            {mobileOpen ? <X size={22} strokeWidth={1.5} /> : <Menu size={22} strokeWidth={1.5} />}
          </button>
          <button aria-label="Search" onClick={() => setSearchOpen(true)} className="p-2.5 rounded-full text-primary hover:text-primary hover:bg-muted/70 transition-all duration-300">
            <Search size={20} strokeWidth={1.5} />
          </button>
        </div>

        {/* CENTER: Logo */}
        <div className="flex items-center justify-center">
          <a
            href={buildStorefrontHomePath(location.search)}
            className="flex-shrink-0 group relative"
            onClick={(e) => { e.preventDefault(); scrollToSection("home"); }}
          >
            <span className="hidden md:block absolute right-full top-1/2 -translate-y-1/2 mr-3 w-8 h-[1px] bg-gradient-to-l from-primary/50 to-transparent" />
            <span className="hidden md:block absolute left-full top-1/2 -translate-y-1/2 ml-3 w-8 h-[1px] bg-gradient-to-r from-primary/50 to-transparent" />
            {logoUrl ? (
              <img
                src={(() => {
                  const m = logoUrl.match(/^(https:\/\/[^/]+\.supabase\.co)\/storage\/v1\/object\/public\/(.+)$/);
                  if (!m) return logoUrl;
                  return `${m[1]}/storage/v1/render/image/public/${m[2]}?width=220&height=84&quality=80&resize=cover&format=origin`;
                })()}
                alt="Kenani Borka Bazar Clothing Shop"
                width={132}
                height={48}
                className={`h-11 md:h-14 w-auto transition-all duration-500 group-hover:scale-105 ${logoReady ? "opacity-100" : "opacity-0"}`}
              />
            ) : (
              <div className="h-11 md:h-14 w-32" aria-hidden="true" />
            )}
          </a>
        </div>

        {/* RIGHT: Action Icons */}
        <div className="flex items-center justify-end gap-0.5">
          <button aria-label="Cart" className="p-2.5 rounded-full text-primary hover:text-primary hover:bg-muted/70 transition-all duration-300 relative" onClick={openCart}>
            <ShoppingBag size={20} strokeWidth={1.5} />
            {totalItems > 0 && (
              <span className="absolute top-1 right-1 w-[18px] h-[18px] rounded-full bg-foreground text-background text-[10px] flex items-center justify-center font-body font-bold animate-fade-in">
                {totalItems}
              </span>
            )}
          </button>
          <button
            onClick={() => { if (!user) navigate("/auth"); else setUserMenuOpen(!userMenuOpen); }}
            className="p-2.5 rounded-full text-primary hover:text-primary hover:bg-muted/70 transition-all duration-300"
            aria-label={user ? "Profile" : "Login"}
          >
            <User size={20} strokeWidth={1.5} />
          </button>
        </div>
      </div>

      {/* ============ DESKTOP HEADER (two-row layout) ============ */}
      <div className="hidden lg:block container mx-auto px-10">
        {/* TOP ROW: Search | Logo | My Account + Cart */}
        <div className="grid grid-cols-[1fr_auto_1fr] items-center h-[88px] gap-4">
          {/* LEFT: Search */}
          <div className="flex items-center justify-start">
            <button
              aria-label="Search"
              onClick={() => setSearchOpen(true)}
              className="flex items-center gap-2 px-4 py-2.5 rounded-full text-primary hover:bg-primary/10 transition-all duration-300 group"
            >
              <Search size={20} strokeWidth={1.5} />
              <span className="text-[10.5px] font-body font-semibold tracking-[0.22em] uppercase">{t("search", "Search")}</span>
            </button>
          </div>

          {/* CENTER: Logo */}
          <div className="flex items-center justify-center">
            <a
              href={buildStorefrontHomePath(location.search)}
              className="flex-shrink-0 group relative"
              onClick={(e) => { e.preventDefault(); scrollToSection("home"); }}
            >
              <span className="absolute right-full top-1/2 -translate-y-1/2 mr-4 w-10 h-[1px] bg-gradient-to-l from-primary/50 to-transparent" />
              <span className="absolute left-full top-1/2 -translate-y-1/2 ml-4 w-10 h-[1px] bg-gradient-to-r from-primary/50 to-transparent" />
              {logoUrl ? (
                <img
                  src={(() => {
                    const m = logoUrl.match(/^(https:\/\/[^/]+\.supabase\.co)\/storage\/v1\/object\/public\/(.+)$/);
                    if (!m) return logoUrl;
                    return `${m[1]}/storage/v1/render/image/public/${m[2]}?width=260&height=96&quality=85&resize=cover&format=origin`;
                  })()}
                  alt="Kenani Borka Bazar Clothing Shop"
                  width={156}
                  height={56}
                  className={`h-16 w-auto transition-all duration-500 group-hover:scale-105 ${logoReady ? "opacity-100" : "opacity-0"}`}
                />
              ) : (
                <div className="h-16 w-40" aria-hidden="true" />
              )}
            </a>
          </div>

          {/* RIGHT: My Account + Cart */}
          <div className="flex items-center justify-end gap-1">
            <div className="relative" ref={userMenuRef}>
              <button
                onClick={() => { if (!user) { navigate("/auth"); return; } setUserMenuOpen(!userMenuOpen); }}
                className="flex items-center gap-2 px-4 py-2.5 rounded-full text-primary hover:bg-primary/10 transition-all duration-300"
                aria-label={user ? "Profile" : "Login"}
              >
                <User size={20} strokeWidth={1.5} />
                <span className="text-[10.5px] font-body font-semibold tracking-[0.22em] uppercase">{t("my_account", "My Account")}</span>
              </button>
              {userMenuOpen && user && (
                <div className="absolute right-0 top-full mt-1 min-w-[180px] bg-background border border-border rounded-md shadow-lg z-[60] py-1.5 animate-fade-in">
                  <button onClick={() => { setUserMenuOpen(false); navigate("/profile"); }} className="w-full text-left px-4 py-2.5 text-sm font-body text-foreground hover:text-primary hover:bg-muted/50 transition-colors flex items-center gap-2.5">
                    <User size={14} strokeWidth={1.5} />
                    <span className="tracking-wider text-xs uppercase">{t("my_account")}</span>
                  </button>
                  <button onClick={() => { setUserMenuOpen(false); navigate("/profile?tab=orders"); }} className="w-full text-left px-4 py-2.5 text-sm font-body text-foreground hover:text-primary hover:bg-muted/50 transition-colors flex items-center gap-2.5">
                    <Package size={14} strokeWidth={1.5} />
                    <span className="tracking-wider text-xs uppercase">{t("orders")}</span>
                  </button>
                  <div className="h-px bg-border mx-2 my-1" />
                  <button onClick={async () => { setUserMenuOpen(false); const sb = await getSupabase(); await sb.auth.signOut(); navigate("/"); }} className="w-full text-left px-4 py-2.5 text-sm font-body text-foreground hover:text-primary hover:bg-muted/50 transition-colors flex items-center gap-2.5">
                    <LogOut size={14} strokeWidth={1.5} />
                    <span className="tracking-wider text-xs uppercase">{t("logout")}</span>
                  </button>
                </div>
              )}
            </div>
            <button
              aria-label="Wishlist"
              onClick={() => navigate("/wishlist")}
              className="p-2.5 rounded-full text-primary hover:bg-primary/10 transition-all duration-300"
            >
              <Heart size={20} strokeWidth={1.5} />
            </button>
            <button
              aria-label="Cart"
              className="flex items-center gap-2 px-4 py-2.5 rounded-full text-primary hover:bg-primary/10 transition-all duration-300 relative"
              onClick={openCart}
            >
              <ShoppingBag size={20} strokeWidth={1.5} />
              <span className="text-[10.5px] font-body font-semibold tracking-[0.22em] uppercase">{t("cart", "Cart")}</span>
              {totalItems > 0 && (
                <span className="ml-0.5 min-w-[20px] h-[20px] px-1.5 rounded-full bg-foreground text-background text-[10px] flex items-center justify-center font-body font-bold">
                  {totalItems}
                </span>
              )}
            </button>
          </div>
        </div>

        {/* BOTTOM ROW: Categories / Navigation */}
        <div className="border-t border-primary/10">
          <nav className="flex items-center justify-center gap-1 h-[48px]">
            {navLinks.map((link) => {
              if ((link as any).isDropdown) {
                return (
                  <div
                    key={link.key}
                    className="relative"
                    onMouseEnter={() => setCatDropdownOpen(true)}
                    onMouseLeave={() => setCatDropdownOpen(false)}
                  >
                    <button
                      onClick={() => setCatDropdownOpen(!catDropdownOpen)}
                      className="relative px-4 py-2 text-[10.5px] font-body font-semibold tracking-[0.22em] uppercase text-foreground hover:text-primary transition-all duration-300 bg-transparent border-none cursor-pointer group flex items-center gap-1"
                    >
                      {link.label}
                      <ChevronDown size={12} className={`transition-transform duration-200 ${catDropdownOpen ? "rotate-180" : ""}`} />
                      <span className="absolute bottom-1 left-1/2 -translate-x-1/2 w-0 h-[1px] bg-primary transition-all duration-300 group-hover:w-2/3" />
                    </button>
                    {catDropdownOpen && (
                      <div className="absolute top-full left-1/2 -translate-x-1/2 mt-0 min-w-[240px] bg-background/98 backdrop-blur-xl border border-primary/20 rounded-lg shadow-2xl z-[60] py-2 animate-fade-in">
                        {categories.length === 0 ? (
                          <p className="px-4 py-2 text-xs text-muted-foreground font-body">No categories</p>
                        ) : (
                          categories.map((cat) => (
                            <button
                              key={cat.id}
                              onClick={() => handleCategoryClick(cat.slug)}
                              className="w-full text-left px-4 py-2.5 text-sm font-body text-foreground hover:text-primary hover:bg-primary/5 transition-colors duration-200 flex items-center gap-3"
                            >
                              {cat.image && (
                                <img src={cat.image} alt={cat.name} width={24} height={24} className="w-6 h-6 rounded-full object-cover flex-shrink-0 ring-1 ring-primary/20" loading="lazy" />
                              )}
                              <span className="tracking-wider text-xs uppercase">{cat.name}</span>
                            </button>
                          ))
                        )}
                        <div className="h-px bg-gradient-to-r from-transparent via-primary/30 to-transparent mx-2 my-1.5" />
                        <button
                          onClick={() => { setCatDropdownOpen(false); navigate("/products"); }}
                          className="w-full text-left px-4 py-2.5 text-sm font-body text-foreground hover:text-primary hover:bg-primary/5 transition-colors duration-200 flex items-center gap-3"
                        >
                          <ShoppingBag size={16} strokeWidth={1.5} className="flex-shrink-0" />
                          <span className="tracking-wider text-xs uppercase">{t("all_products", "All Products")}</span>
                        </button>
                      </div>
                    )}
                  </div>
                );
              }
              return (
                <button
                  key={link.key}
                  onMouseEnter={() => (link as any).isPage && handlePrefetch(link.target)}
                  onTouchStart={() => (link as any).isPage && handlePrefetch(link.target)}
                  onClick={() => {
                    if ((link as any).isPage) navigate(`/${link.target}`);
                    else scrollToSection(link.target);
                  }}
                  className="relative px-4 py-2 text-[10.5px] font-body font-semibold tracking-[0.22em] uppercase text-foreground hover:text-primary transition-all duration-300 bg-transparent border-none cursor-pointer group"
                >
                  {link.label}
                  <span className="absolute bottom-1 left-1/2 -translate-x-1/2 w-0 h-[1px] bg-primary transition-all duration-300 group-hover:w-2/3" />
                </button>
              );
            })}
          </nav>
        </div>
      </div>

      {/* Mobile Nav — Premium slide-in drawer */}
      {/* Backdrop */}
      <div
        onClick={() => setMobileOpen(false)}
        className={`lg:hidden fixed inset-0 top-0 bg-black/60 backdrop-blur-sm z-40 transition-opacity duration-300 ${
          mobileOpen ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"
        }`}
        aria-hidden="true"
      />
      {/* Drawer panel */}
      <div
        ref={mobileMenuRef}
        className={`lg:hidden fixed top-0 left-0 h-[100dvh] w-[82%] max-w-[340px] bg-background z-50 shadow-2xl transition-transform duration-400 ease-out border-r border-primary/20 flex flex-col ${
          mobileOpen ? "translate-x-0" : "-translate-x-full"
        }`}
        style={customHeaderStyles}
      >
        {/* Drawer header */}
        <div className="relative flex items-center justify-between px-5 py-4 border-b border-primary/15">
          <div className="absolute inset-x-0 top-0 h-[1px] bg-gradient-to-r from-transparent via-primary/60 to-transparent" />
          {logoUrl && (
            <img src={logoUrl} alt="Logo" className="h-9 w-auto" />
          )}
          <button
            onClick={() => setMobileOpen(false)}
            aria-label="Close menu"
            className="p-2 rounded-full text-primary hover:bg-primary/10 transition-all"
          >
            <X size={20} strokeWidth={1.5} />
          </button>
        </div>

        {/* Drawer body */}
        <div className="flex-1 overflow-y-auto px-2 py-4">
          <div className="flex flex-col gap-0.5">
            {navLinks.map((link, i) => {
              if ((link as any).isDropdown) {
                return (
                  <div key={link.key} className="px-2">
                    <button
                      onClick={() => setMobileCatOpen(!mobileCatOpen)}
                      className="w-full text-left px-3 py-3.5 rounded-lg text-[12px] font-body font-semibold tracking-[0.18em] uppercase text-foreground hover:text-primary hover:bg-primary/5 transition-all bg-transparent border-none cursor-pointer flex items-center justify-between"
                    >
                      <span>{link.label}</span>
                      <ChevronDown size={14} className={`transition-transform duration-200 ${mobileCatOpen ? "rotate-180" : ""}`} />
                    </button>
                    <div className={`overflow-hidden transition-all duration-300 ${mobileCatOpen ? "max-h-[500px] opacity-100" : "max-h-0 opacity-0"}`}>
                      <div className="pl-3 py-1.5 flex flex-col gap-0.5 border-l border-primary/15 ml-3">
                        {categories.map((cat) => (
                          <button
                            key={cat.id}
                            onClick={() => handleCategoryClick(cat.slug)}
                            className="text-left px-3 py-2 rounded-md text-[11px] font-body tracking-wider uppercase text-foreground/80 hover:text-primary hover:bg-primary/5 transition-all bg-transparent border-none cursor-pointer flex items-center gap-2.5"
                          >
                            {cat.image && (
                              <img src={cat.image} alt={cat.name} className="w-6 h-6 rounded-full object-cover ring-1 ring-primary/20" loading="lazy" />
                            )}
                            {cat.name}
                          </button>
                        ))}
                        <button
                          onClick={() => { setMobileCatOpen(false); setMobileOpen(false); navigate("/products"); }}
                          className="text-left px-3 py-2 rounded-md text-[11px] font-body tracking-wider uppercase text-foreground/80 hover:text-primary hover:bg-primary/5 transition-all bg-transparent border-none cursor-pointer flex items-center gap-2.5"
                        >
                          <ShoppingBag size={14} strokeWidth={1.5} />
                          {t("all_products", "All Products")}
                        </button>
                      </div>
                    </div>
                  </div>
                );
              }
              return (
                <div key={link.key} className="px-2">
                  <button
                    onClick={() => {
                      setMobileOpen(false);
                      if ((link as any).isPage) {
                        navigate(`/${link.target}`);
                      } else {
                        scrollToSection(link.target);
                      }
                    }}
                    className="w-full px-3 py-3.5 rounded-lg text-[12px] font-body font-semibold tracking-[0.18em] uppercase text-foreground hover:text-primary hover:bg-primary/5 transition-all bg-transparent border-none cursor-pointer text-left"
                  >
                    {link.label}
                  </button>
                </div>
              );
            })}
          </div>

          {/* Quick action row */}
          <div className="mt-5 mx-4 pt-4 border-t border-primary/15 grid grid-cols-2 gap-2">
            <button
              onClick={() => { setMobileOpen(false); navigate("/wishlist"); }}
              className="flex items-center justify-center gap-2 py-2.5 rounded-lg border border-primary/20 text-[10.5px] font-body font-semibold tracking-[0.15em] uppercase text-foreground hover:bg-primary/5 hover:text-primary transition-all"
            >
              <Heart size={14} strokeWidth={1.5} />
              {t("wishlist", "Wishlist")}
            </button>
            <button
              onClick={() => { setMobileOpen(false); if (!user) { navigate("/auth"); } else { navigate("/profile"); } }}
              className="flex items-center justify-center gap-2 py-2.5 rounded-lg border border-primary/20 text-[10.5px] font-body font-semibold tracking-[0.15em] uppercase text-foreground hover:bg-primary/5 hover:text-primary transition-all"
            >
              <User size={14} strokeWidth={1.5} />
              {user ? t("my_account", "Account") : t("login", "Login")}
            </button>
          </div>
        </div>

        {/* Drawer footer accent */}
        <div className="h-[1px] bg-gradient-to-r from-transparent via-primary/60 to-transparent" />
      </div>
    </header>

    {searchOpen && <Suspense fallback={null}><SearchModal open={searchOpen} onClose={() => setSearchOpen(false)} /></Suspense>}
    </>
  );
});

Header.displayName = "Header";

export default Header;
